import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { Label } from '../../../components/ui/label';
import countriesConfig from '../../../shared-config/countries.config.json';

// Type for country data from config
interface CountryConfigData {
  name: string;
  currency: string;
  language: string;
  numberFormat: string;
  dateFormat: string;
}

// Flag component using country-flag-icons CDN
function CountryFlag({ code, size = 20 }: { code: string; size?: number }) {
  const flagUrl = `https://purecatamphetamine.github.io/country-flag-icons/3x2/${code}.svg`;
  return (
    <img
      src={flagUrl}
      alt={code}
      width={size * 1.5}
      height={size}
      style={{ objectFit: 'cover', borderRadius: 2 }}
      loading="lazy"
    />
  );
}

export interface CountryOption {
  code: string;
  name: string;
  currency: string;
  language: string;
  numberFormat: string;
  dateFormat: string;
}

interface CountryPickerProps {
  value: string | null;
  onChange: (code: string | null) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default';
  className?: string;
  /** Show a "clear" option at the top of the list to reset to null. Label uses placeholder text. */
  allowClear?: boolean;
}

// Build sorted country options from config
const countryOptions: CountryOption[] = Object.entries(
  countriesConfig.countries as Record<string, CountryConfigData>
)
  .map(([code, data]) => ({
    code,
    name: data.name,
    currency: data.currency,
    language: data.language,
    numberFormat: data.numberFormat,
    dateFormat: data.dateFormat,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

// Map for quick lookup
const countryMap = new Map(countryOptions.map((c) => [c.code, c]));

export function CountryPicker({
  value,
  onChange,
  label,
  placeholder,
  helperText,
  error,
  errorMessage,
  disabled,
  required,
  fullWidth = true,
  size = 'default',
  className,
  allowClear = false,
}: CountryPickerProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const resolvedPlaceholder = placeholder ?? t('pickers.selectCountry');

  const selectedOption = useMemo(
    () => (value ? countryMap.get(value) || null : null),
    [value]
  );

  return (
    <div className={cn(fullWidth && 'w-full', className)}>
      {label && (
        <Label className="mb-1.5 block">
          {label}
          {required && <span className="text-destructive ms-1">*</span>}
        </Label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'justify-between font-normal',
              fullWidth && 'w-full',
              size === 'sm' && 'h-8 text-sm',
              !selectedOption && 'text-muted-foreground',
              error && 'border-destructive'
            )}
          >
            {selectedOption ? (
              <span className="flex items-center gap-2">
                <CountryFlag code={selectedOption.code} size={16} />
                <span className="truncate">{selectedOption.name}</span>
              </span>
            ) : (
              resolvedPlaceholder
            )}
            <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] max-w-[calc(100vw-2rem)] p-0" align="start">
          <Command>
            <CommandInput placeholder={t('pickers.searchCountry')} />
            <CommandList>
              <CommandEmpty>{t('pickers.noCountryFound')}</CommandEmpty>
              <CommandGroup>
                {allowClear && (
                  <CommandItem
                    value="__clear__"
                    onSelect={() => {
                      onChange(null);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'me-2 h-4 w-4',
                        !value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <span className="text-muted-foreground">{resolvedPlaceholder}</span>
                  </CommandItem>
                )}
                {countryOptions.map((option) => (
                  <CommandItem
                    key={option.code}
                    value={`${option.name} ${option.code}`}
                    onSelect={() => {
                      onChange(option.code === value ? null : option.code);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'me-2 h-4 w-4',
                        value === option.code ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <CountryFlag code={option.code} size={16} />
                    <span className="ms-2 flex-1">{option.name}</span>
                    <span className="text-muted-foreground text-xs">{option.code}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {(errorMessage || helperText) && (
        <p className={cn(
          'text-xs mt-1',
          error || errorMessage ? 'text-destructive' : 'text-muted-foreground'
        )}>
          {errorMessage || helperText}
        </p>
      )}
    </div>
  );
}

// Export helper functions and data for external use
export function getCountryByCode(code: string): CountryOption | undefined {
  return countryMap.get(code);
}

export function getCountryName(code: string): string {
  return countryMap.get(code)?.name || code;
}

export function getAllCountries(): CountryOption[] {
  return countryOptions;
}

export { CountryFlag };
